/**
 * Tracks operator activity for the federated access provider's inactivity
 * timeout (`federatedAccessProvider.ts`). A single in-memory timestamp per
 * instance — never persisted, same discipline as the session token itself
 * (see that file's header comment). Each browser tab creates its own
 * provider, and therefore its own tracker: activity in one tab must never
 * keep another tab's session alive, nor the other way round.
 */
const ACTIVITY_EVENT_TYPES = ['pointerdown', 'keydown', 'wheel', 'scroll'] as const;

export interface ActivityTracker {
  /**
   * Records activity that did not come through a DOM listener — a real call
   * to the payment platform API is the most reliable signal of all that an
   * operator is genuinely working (see the wave report, §4).
   */
  markActivity(): void;
  /** True once at least `thresholdMs` have passed since the last recorded activity. */
  isIdle(thresholdMs: number): boolean;
  /** Detaches every listener this tracker installed. */
  stop(): void;
}

type DocumentLike = Pick<Document, 'addEventListener' | 'removeEventListener' | 'visibilityState'>;

/**
 * Wires passive, debounced listeners for real operator interaction —
 * pointer, keyboard, wheel/scroll, and the tab regaining focus. `target`
 * and `documentTarget` default to the real `window`/`document`; tests pass
 * isolated fakes so two simulated tabs never share activity state.
 */
export function createActivityTracker(
  target: EventTarget = window,
  documentTarget: DocumentLike = document,
  throttleMs = 2_000,
): ActivityTracker {
  let lastActivityAt = Date.now();

  const markActivity = (): void => {
    const now = Date.now();
    // Debounced on purpose: a single mark per throttle window, never one
    // write per event — a wheel/scroll burst would otherwise touch this on
    // every frame for no benefit (see the wave report, §4 "qué cuenta como
    // actividad").
    if (now - lastActivityAt < throttleMs) return;
    lastActivityAt = now;
  };

  const onVisibilityChange = (): void => {
    if (documentTarget.visibilityState === 'visible') markActivity();
  };

  const listenerOptions: AddEventListenerOptions = { passive: true };
  for (const type of ACTIVITY_EVENT_TYPES) {
    target.addEventListener(type, markActivity, listenerOptions);
  }
  documentTarget.addEventListener('visibilitychange', onVisibilityChange);

  return {
    markActivity,
    isIdle(thresholdMs) {
      return Date.now() - lastActivityAt >= thresholdMs;
    },
    stop() {
      for (const type of ACTIVITY_EVENT_TYPES) {
        target.removeEventListener(type, markActivity);
      }
      documentTarget.removeEventListener('visibilitychange', onVisibilityChange);
    },
  };
}
